import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { es, type Clave } from "../idiomas/es";
import { ca } from "../idiomas/ca";
import { en } from "../idiomas/en";
import { nl } from "../idiomas/nl";
import { de } from "../idiomas/de";
import { claveDelAmbito } from "./ambito";

/**
 * El idioma de la pantalla.
 *
 * Cada frase vive en un diccionario por idioma (src/idiomas) y la pantalla la
 * pide por su clave: `t("entrada.numeroSocio")`. Las variables van entre
 * llaves en el texto y se pasan aparte: `t("inicio.cuotaVenceEn", { dias })`.
 *
 * Quién decide el idioma, por orden: lo que la persona eligió en este
 * navegador (se guarda), el idioma del club (lo pone el club en Ajustes, es
 * lo que ve un socio la primera vez), y si no hay ninguno de los dos, el del
 * navegador. Un socio de un club catalán abre el portal en catalán sin tocar
 * nada; si prefiere castellano, lo cambia una vez y se queda.
 *
 * Hoy lo usa el portal del socio. La aplicación de gestión sigue en
 * castellano; cuando se traduzca, será con esto mismo.
 */

/*
 * La lista manda: el tipo, el guardian y los selectores salen de aqui, asi que
 * añadir un idioma es añadir una linea y su diccionario. El nombre va en su
 * propio idioma, que es como lo reconoce quien lo busca.
 */
export const IDIOMAS = [
  { codigo: "es", nombre: "Español" },
  { codigo: "ca", nombre: "Català" },
  { codigo: "en", nombre: "English" },
  { codigo: "nl", nombre: "Nederlands" },
  { codigo: "de", nombre: "Deutsch" },
] as const;

export type Idioma = (typeof IDIOMAS)[number]["codigo"];

const DICCIONARIOS: Record<Idioma, Record<Clave, string>> = { es, ca, en, nl, de };
/*
 * Lo que la persona elige se guarda por ámbito, igual que el token: la gestión
 * del club, la web pública y el portal del socio comparten navegador y no
 * comparten esta decisión.
 */
function claveGuardada(): string {
  return claveDelAmbito("idioma");
}

export type Variables = Record<string, string | number>;

/** Sustituye {nombre} por su valor. Lo que no tenga valor se queda tal cual, que se vea. */
export function rellenar(texto: string, variables?: Variables): string {
  if (!variables) return texto;
  return texto.replace(/\{(\w+)\}/g, (todo, nombre: string) =>
    nombre in variables ? String(variables[nombre]) : todo,
  );
}

export function esIdioma(valor: unknown): valor is Idioma {
  return IDIOMAS.some((i) => i.codigo === valor);
}

/** El del navegador si lo tenemos; si no, castellano, que es el de casa. */
function idiomaDelNavegador(): Idioma {
  const lengua = (typeof navigator !== "undefined" ? navigator.language : "").toLowerCase();
  return IDIOMAS.find((i) => lengua.startsWith(i.codigo))?.codigo ?? "es";
}

/**
 * El idioma sin React, para lo que corre antes de montar: el titulo de la
 * pestaña se pone al cargar el fichero, cuando todavia no hay contexto.
 */
export function idiomaActual(): Idioma {
  return idiomaGuardado() ?? idiomaDelNavegador();
}

function idiomaGuardado(): Idioma | null {
  try {
    const valor = localStorage.getItem(claveGuardada());
    return esIdioma(valor) ? valor : null;
  } catch {
    return null;
  }
}

/**
 * Al salir del portal: la sesión se acaba y quien entre detrás es otro.
 *
 * Hace falta en la tablet del mostrador, donde entran los socios uno detrás de
 * otro: sin esto, el siguiente vería el portal en el idioma del anterior.
 */
export function olvidarIdiomaGuardado(): void {
  try {
    localStorage.removeItem(claveGuardada());
  } catch {
    // Sin almacenamiento no había nada que olvidar.
  }
}

/**
 * «Español · Català · English», cada uno en su idioma, que es como lo reconoce
 * quien lo busca. Sale de la lista para que no haya que reescribir las frases
 * de la web cada vez que entra un idioma nuevo.
 */
export function nombresDeIdiomas(): string {
  return IDIOMAS.map((i) => i.nombre).join(" · ");
}

/** Traduce una clave sin contexto, para lo de antes de montar. */
export function traducir(clave: Clave, idioma: Idioma = idiomaActual()): string {
  return DICCIONARIOS[idioma][clave];
}

/**
 * La clave y los datos de un fallo, si los trae.
 *
 * Se mira la forma y no la clase a proposito: asi este fichero no depende de
 * lib/api, que es lo que las pruebas sustituyen por un doble.
 */
function claveDelFallo(fallo: unknown): { clave: string; datos?: Variables } | null {
  if (typeof fallo !== "object" || fallo === null) return null;
  const f = fallo as { clave?: unknown; datos?: unknown };
  if (typeof f.clave !== "string" || !f.clave) return null;
  const datos = typeof f.datos === "object" && f.datos !== null ? (f.datos as Variables) : undefined;
  return { clave: f.clave, datos };
}

/** Los huecos que la fila trae guardados en JSON, si trae alguno que valga. */
function huecosGuardados(datos: string | null | undefined): Variables | undefined {
  if (!datos) return undefined;
  try {
    const leido: unknown = JSON.parse(datos);
    return typeof leido === "object" && leido !== null ? (leido as Variables) : undefined;
  } catch {
    // Un JSON roto no puede dejar la pantalla en blanco: se pinta sin rellenar.
    return undefined;
  }
}

interface ValorIdioma {
  idioma: Idioma;
  t: (clave: Clave, variables?: Variables) => string;
  /** Un texto que manda el servidor en castellano, traducido si se conoce. */
  delServidor: (texto: string | null | undefined) => string;
  /**
   * Lo que salió mal, dicho en el idioma de quien lo lee.
   *
   * El servidor manda su frase en castellano y, cuando sabe decirlo de las dos
   * formas, la clave con la que decirlo aquí y los datos de sus huecos. Sin
   * clave se enseña la frase del servidor, que es mejor que un «algo ha ido
   * mal»: el socio al menos se la puede leer por teléfono a alguien del club.
   *
   * Hace falta porque el portal estaba entero en el idioma del socio hasta que
   * algo le salía mal, y justo entonces le hablaba en otro. Donde más dolía era
   * al entrar y al pedir el alta, que es lo primero que hace y lo hace solo.
   */
  delFallo: (fallo: unknown, siNoSeSabe: Clave) => string;
  /**
   * El concepto de un apunte, dicho en el idioma de quien lo lee.
   *
   * La fila trae su clave cuando el texto lo escribió el programa, y entonces se
   * rehace aquí con los datos de sus huecos. Cuando no la trae es porque lo
   * escribió el club —o porque es una fila de antes de que se guardara—, y
   * entonces se enseña tal cual: lo que escribe el club no se traduce, igual que
   * sus notas o los nombres de sus categorías.
   */
  delConcepto: (apunte: {
    concepto?: string | null;
    conceptoClave?: string | null;
    conceptoDatos?: string | null;
  }) => string;
  /** «3 días» / «3 dies». */
  dias: (n: number) => string;
  /**
   * «1 socio» / «3 socios», con la frase entera en cada caso.
   *
   * Se le dan las dos claves —la del uno y la del resto, que lleva {n}— en vez
   * de una palabra y su plural: hay idiomas donde no cambia solo la palabra.
   */
  cuenta: (n: number, uno: Clave, varios: Clave) => string;
  /** Lo eligió la persona: se guarda para este navegador. */
  cambiar: (idioma: Idioma) => void;
  /**
   * Si el idioma de ahora lo eligió la persona en este aparato, o es solo el
   * que le toca por defecto. Los dos se ven igual en pantalla y no lo son: la
   * solicitud de alta solo se queda con el idioma si lo eligió quien la manda.
   */
  loEligio: boolean;
  /** El idioma del club: manda solo si la persona no ha elegido nunca. */
  aplicarPorDefecto: (idioma: Idioma | null | undefined) => void;
  /**
   * El idioma que el servidor tiene guardado para este socio, que manda sobre
   * lo que quedara elegido en este aparato.
   *
   * Es el orden bueno porque el del servidor es la última decisión que tomó
   * alguien a propósito —el socio desde cualquier teléfono, o el equipo desde
   * su ficha—, y lo de este aparato puede ser de hace meses o del socio
   * anterior que usó la tablet del mostrador. Tocar el selector vuelve a
   * mandar, que para eso está.
   */
  aplicarDelSocio: (idioma: Idioma | null | undefined) => void;
}

const Contexto = createContext<ValorIdioma | null>(null);

export function ProveedorIdioma({
  children,
  inicial,
}: {
  children: ReactNode;
  inicial?: Idioma;
}) {
  const [elegido, setElegido] = useState<Idioma | null>(idiomaGuardado);
  const [porDefecto, setPorDefecto] = useState<Idioma>(
    inicial ?? idiomaDelNavegador,
  );
  const [delSocio, setDelSocio] = useState<Idioma | null>(null);
  const idioma = delSocio ?? elegido ?? porDefecto;

  const cambiar = useCallback((nuevo: Idioma) => {
    setElegido(nuevo);
    // Lo que acaba de tocar manda sobre lo que trajo el servidor al abrir.
    setDelSocio(null);
    try {
      localStorage.setItem(claveGuardada(), nuevo);
    } catch {
      // Sin almacenamiento, vale para esta visita.
    }
  }, []);

  const aplicarPorDefecto = useCallback((nuevo: Idioma | null | undefined) => {
    if (esIdioma(nuevo)) setPorDefecto(nuevo);
  }, []);

  const aplicarDelSocio = useCallback((nuevo: Idioma | null | undefined) => {
    setDelSocio(esIdioma(nuevo) ? nuevo : null);
  }, []);

  const valor = useMemo<ValorIdioma>(() => {
    const diccionario = DICCIONARIOS[idioma];
    const t = (clave: Clave, variables?: Variables) =>
      rellenar(diccionario[clave] ?? es[clave] ?? clave, variables);
    return {
      idioma,
      t,
      delServidor: (texto) => {
        if (!texto) return "";
        const clave = `servidor.${texto}` as Clave;
        return diccionario[clave] ?? texto;
      },
      delFallo: (fallo, siNoSeSabe) => {
        const suyo = claveDelFallo(fallo);
        if (suyo) {
          const frase = diccionario[suyo.clave as Clave];
          if (frase) return rellenar(frase, suyo.datos);
        }
        if (fallo instanceof Error && fallo.message) return fallo.message;
        return t(siNoSeSabe);
      },
      delConcepto: (apunte) => {
        const frase = apunte.conceptoClave ? diccionario[apunte.conceptoClave as Clave] : undefined;
        if (!frase) return apunte.concepto ?? "";
        return rellenar(frase, huecosGuardados(apunte.conceptoDatos));
      },
      dias: (n) => `${n} ${t(n === 1 ? "unidad.dia" : "unidad.dias")}`,
      cuenta: (n, uno, varios) => (n === 1 ? t(uno) : t(varios, { n })),
      cambiar,
      loEligio: elegido !== null,
      aplicarPorDefecto,
      aplicarDelSocio,
    };
  }, [idioma, elegido, cambiar, aplicarPorDefecto, aplicarDelSocio]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useIdioma(): ValorIdioma {
  const valor = useContext(Contexto);
  if (!valor)
    throw new Error("useIdioma debe usarse dentro de ProveedorIdioma");
  return valor;
}

/**
 * El selector: un botón por idioma, el de ahora marcado.
 *
 * `alElegir` es para quien tenga que guardar la elección en algún sitio más.
 * Sale del gesto de tocar el botón a propósito, y no de mirar el idioma de
 * ahora: lo segundo no distingue entre lo que acaba de elegir esta persona y
 * lo que quedó guardado en este aparato de otra visita o de otro socio.
 */
export function SelectorIdioma({
  compacto,
  alElegir,
}: {
  compacto?: boolean;
  alElegir?: (idioma: Idioma) => void;
}) {
  const { idioma, cambiar } = useIdioma();
  return (
    <div className="selector-idioma" role="group" aria-label="Idioma">
      {IDIOMAS.map((i) => (
        <button
          key={i.codigo}
          type="button"
          className={idioma === i.codigo ? "activo" : ""}
          aria-pressed={idioma === i.codigo}
          onClick={() => {
            cambiar(i.codigo);
            alElegir?.(i.codigo);
          }}
          lang={i.codigo}
        >
          {compacto ? i.codigo.toUpperCase() : i.nombre}
        </button>
      ))}
    </div>
  );
}
